import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { APP_INITIALIZER, Inject, LOCALE_ID, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment, G_MAPS_KEY_VALUE, YA_API_KEY_VALUE } from 'environments/environment';
import { PlatformModule } from '@angular/cdk/platform';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { PageComponent } from './pages/page.component';
import { JWTInterceptor } from './services/token-interceptor.service';
import { G_MAPS_API_KEY_TOKEN } from './tokens/g-maps.token';
import { SharedModule } from './shared/shared.module';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { appReducer } from './app.reducer';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OrganizationEffects } from './store/organization.effects';
import { ApiService } from './services/api.service';
import { UserEffects } from './store/user.effects';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { storageMetaReducer } from './store/storage.meta-reducer';
import { YA_API_KEY_TOKEN } from './tokens/ya-api.token';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { APP_BASE_HREF } from '@angular/common';
import { APP_ORGANIZATION_CODE, appInitializer, getInitData, localeInitializer } from 'app/common';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { AnalyticsService } from 'app/services/analytics.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// парсим Url и пытаемся понять, какую организацию мы тут будем сейчас показывать
const initData = getInitData(window);

@NgModule({
  declarations: [
    AppComponent,
    PageComponent,
  ],
  imports: [
    BrowserModule,
    HammerModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    PlatformModule,
    MatInputModule,
    MatFormFieldModule,
    MatSnackBarModule,
    MatCheckboxModule,
    // ngrx
    StoreModule.forRoot(appReducer, {
      metaReducers: [
        storageMetaReducer
      ]
    }),
    EffectsModule.forRoot([
      OrganizationEffects,
      UserEffects
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
    }),
    // https://github.com/ngx-translate/core/issues/1193
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      isolate: false
    }),

    SharedModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JWTInterceptor,
      multi: true,
    },
    {
      provide: G_MAPS_API_KEY_TOKEN,
      useValue: G_MAPS_KEY_VALUE,
    },
    {
      provide: YA_API_KEY_TOKEN,
      useValue: YA_API_KEY_VALUE, // insert commercial yandex map api key
    },
    ApiService,
    {provide: Window, useValue: window},
    {
      provide: APP_BASE_HREF, useValue: initData.baseHref
    },
    {
      provide: APP_ORGANIZATION_CODE, useValue: initData.organizationCode
    },
    {
      provide: LOCALE_ID,
      useExisting: false,
      deps: [
        [new Inject(Store)],
      ],
      useFactory: localeInitializer,
    },
    OrganizationEffects,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: appInitializer,
      deps: [
        [new Inject(Store)],
        APP_ORGANIZATION_CODE,
        AnalyticsService,
        TranslateService
      ]
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
